/**
 * A couple on one invoice (0215): who a client bills with, behind the
 * combined-billing line on the Billing details card.
 *
 *   GET /api/t/{slug}/v1/clients/{clientId}/billing-household  (view_invoice)
 *   PUT  same, body { bills_with_client_id: uuid | null }       (set_bill_to)
 *
 * Every fetch failure throws ApiError through apiErrorFrom.
 */

import { writeFetch } from './writeFetch';
import { useQuery } from '@tanstack/react-query';
import { apiErrorFrom } from '@/lib/api';

export interface BillingHouseholdWire {
  client_id: string;
  bills_with: { client_id: string; display_name: string } | null;
  billed_with_me: Array<{ client_id: string; display_name: string }>;
  qbo_link_unused: boolean;
}

export function billingHouseholdKey(slug: string, clientId: string): readonly unknown[] {
  return ['client-billing-household', slug, clientId];
}

function householdPath(slug: string, clientId: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(clientId)}/billing-household`;
}

export async function fetchBillingHousehold(
  slug: string,
  clientId: string,
  signal?: AbortSignal,
): Promise<BillingHouseholdWire> {
  const path = householdPath(slug, clientId);
  const res = await fetch(path, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  if (!res.ok) throw await apiErrorFrom(res, `GET ${path}`);
  return (await res.json()) as BillingHouseholdWire;
}

export async function putBillingHousehold(
  slug: string,
  clientId: string,
  billsWithClientId: string | null,
): Promise<BillingHouseholdWire> {
  const path = householdPath(slug, clientId);
  const res = await writeFetch(path, {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ bills_with_client_id: billsWithClientId }),
    redirect: 'manual',
  });
  if (!res.ok) throw await apiErrorFrom(res, `PUT ${path}`);
  return (await res.json()) as BillingHouseholdWire;
}

export function useBillingHousehold(slug: string, clientId: string, enabled = true) {
  return useQuery<BillingHouseholdWire>({
    queryKey: billingHouseholdKey(slug, clientId),
    queryFn: ({ signal }) => fetchBillingHousehold(slug, clientId, signal),
    enabled: enabled && slug !== '' && clientId !== '',
  });
}
